'use client';

import { useSWRConfig } from 'swr';
import useReviewModal from '@/app/hooks/useReviewModal'
import ReviewModal from '@/app/components/modals/ReviewModal'
import { formatCurrency, formatDate, formatStatus } from '../utils/format';
import toast from "react-hot-toast";
import apiService from "../services/apiService";
import { getUserId } from "../lib/actions";
import { useEffect, useState } from 'react';
import { useContract } from "./useContract";
import { loadStripe } from "@stripe/stripe-js";
import Image from "next/image";
import Link from "next/link";
import ContactButton from "../components/ContactButton";
import ReservationData from './ReservationData';
import { ReservationCardProps } from '@/types/type';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

export default function ReservationCard({ reservation, user, refreshUserData }: ReservationCardProps) {
  
  const { contract } = useContract(reservation.id);
  const { mutate } = useSWRConfig();
  const [loadingCancelId, setLoadingCancelId] = useState<string | null>(null);

  const handleCheckout = async (reservationId: string) => {
    const stripe = await stripePromise;
    try {
      const res = await apiService.postWithToken('/api/user/stripe/checkout/', {
        reservation_id: reservationId,
        checkout_type: "reservation",
    });

      if (res?.sessionId) {
        await stripe?.redirectToCheckout({ sessionId: res.sessionId });
      } else {
        alert("Erro ao iniciar pagamento. Tente novamente.");
      }
    } catch (err) {
      console.error("Erro no checkout:", err);
      alert("Ocorreu um erro ao redirecionar para o pagamento.");
    }
  };

  const handleReclaimCredit = async (reservationId: string) => {
    try {
      const res = await apiService.postWithToken(
        `/api/user/reservations/${reservationId}/reclaim-credit/`,
        {}
      );
      toast.success(`Crédito de ${formatCurrency(res.credited_amount)} devolvido com sucesso!`);
    } catch (error: any) {
      let message = "Erro ao cancelar reserva e restaurar crédito.";
        if (error?.response?.data?.error) {
          message = error.response.data.error;
        } else if (typeof error === 'object' && 'message' in error) {
          message = error.message;
        }
        toast.error(message);
    }
  };

  const handleCancelAndRestoreCredit = async (reservationId: string) => {
    setLoadingCancelId(reservationId);
    try {
      const res = await apiService.postWithToken(
        `/api/reservations/${reservationId}/cancel-and-restore/`,
        {}
      );
      toast.success(res?.message ?? 'Reserva cancelada e crédito restaurado com sucesso!');

      // Recarrega as reservas ativas
      mutate('/api/auth/myreservations/');
      mutate('/api/user/dashboard/');
    } catch (error: any) {
      const message = error?.message || "Erro ao cancelar reserva e restaurar crédito.";
      toast.error(message);
    } finally {
      setLoadingCancelId(null);
    }
  };

  const isValid = (r: any) =>
    !r.is_paid &&
    r.status === "pending" &&
    new Date(r.expires_at) > new Date();
  
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserId = async () => {
      const id = await getUserId();
      setUserId(id);
    };
    fetchUserId();
  }, []);
  
  const canContactVendor = ['confirmed', 'pending_dispute'].includes(reservation.status);

  const reviewModal = useReviewModal()

  return (
    <div className={`p-5 grid grid-cols-1 md:grid-cols-4 gap-4 shadow-md rounded-xl 
      ${reservation.status === 'cancelled' 
        ? 'bg-red-50 border border-red-300' 
        : 'bg-white border border-gray-300'}
    `}>
      {/* Imagem do produto */}
      <div className="col-span-1">
        <div className="relative w-full h-40 md:h-full aspect-square overflow-hidden rounded-xl">
          <Image
            src={reservation.product.image_url}
            alt={`Imagem de ${reservation.product.title}`}
            fill
            className="object-cover hover:scale-110 transition-transform duration-300"
          />
        </div>
      </div>

      {/* Conteúdo da reserva */}
      <div className="col-span-3 flex flex-col justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-1">{reservation.product.title}</h2>
          {reservation.status === 'canceled' && (
            <span className="inline-block text-sm text-red-600 bg-red-100 px-2 py-1 rounded-md mb-2">
              Cancelada
            </span>
          )}
          <p className="text-sm text-gray-600 mb-1">
            {formatDate(reservation.start_date)} até {formatDate(reservation.end_date)} — {reservation.number_of_nights} noite{reservation.number_of_nights > 1 ? 's' : ''}
          </p>
          <p className="text-sm font-bold text-green-800">
            Valor total: {formatCurrency(reservation.total_price)}
          </p>
          <p className="text-sm">
            Status: {formatStatus(reservation.status)}
          </p>
          <ReservationData user={user} refreshUserData={refreshUserData} />
        </div>

        {/* Ações */}
        <div className="flex flex-wrap gap-4 mt-6">
          {isValid(reservation) && (
            <button
              onClick={() => handleCheckout(reservation.id)}
              className="py-3 px-5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition"
            >
              Pagar agora
            </button>
          )}

          {reservation.status?.toLowerCase() === "confirmed" && reservation.contract && (
            <Link 
              href={`/myreservations/${reservation.id}/contract`}
              className="py-3 px-5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition"
            >
              Ver Contrato
            
            </Link>
          )}

          {reservation.status?.toLowerCase() === "confirmed" && !reservation.contract && (
            <p className="text-xs text-gray-400 italic">Contrato ainda não disponível</p>
          )}

          {reservation.status === "confirmed" && !contract && (
            <div className="bg-green-100 text-green-800 text-sm font-semibold p-2 py-1 rounded-xl">
              Confirmada
              <div className='mt-2 text-center text-lg'>
                <span>💎</span>
              </div>
            </div>
            
          )}

          {reservation.status === "pending_dispute" && !reservation.is_credit_reclaimed && (
            <button
              onClick={() => handleReclaimCredit(reservation.id)}
              className="py-3 px-5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl transition"
            >
              Reaver crédito
            </button>
          )}

          {reservation.payment_type === 'credit' && reservation.status === 'confirmed' && !reservation.contract && (
            <button
              onClick={() => handleCancelAndRestoreCredit(reservation.id)}
              disabled={loadingCancelId === reservation.id}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm"
            >
              {loadingCancelId === reservation.id
                ? "Cancelando..."
                : "Cancelar reserva e restaurar crédito"}
            </button>
          )}

          {userId && userId !== reservation.vendor?.id && (
            <ContactButton
              userId={userId}
              vendorId={reservation.vendor?.id}
              disabled={!canContactVendor}
            />
          )}
          {reservation.can_review && (
            <>
              <button
                onClick={reviewModal.open}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
              >
                Avaliar
              </button>
              <ReviewModal reservationId={reservation.id} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
